import { createProductSchema, createBreadcrumbSchema } from './schemaUtils';

/**
 * Generate SEO-optimized title for products
 */
export function generateProductSeoTitle(productTitle: string, location?: string, category?: string): string {
  let title = productTitle;
  
  if (location) {
    title += ` në ${location}`;
  }
  
  if (category) {
    title += ` | ${category}`;
  }
  
  title += ' - TreguIm';
  
  // Limit title length for SEO
  return title.length > 60 ? title.substring(0, 57) + '...' : title;
}

/**
 * Generate SEO-optimized description for products
 */
export function generateProductSeoDescription(
  productTitle: string, 
  price: number, 
  location?: string, 
  condition?: string,
  category?: string
): string {
  let description = `Shesblej dhe merrjep ${productTitle}`;
  
  if (condition) {
    description += ` ${condition === 'new' ? 'të ri' : 'të përdorur'}`;
  }
  
  description += ` për ${price} Lekë`;
  
  if (location) {
    description += ` në ${location}`;
  }
  
  if (category) {
    description += `. ${category}`;
  }
  
  description += ' në TreguIm - Celsi platforma më e madhe për njoftime falas në Shqipëri.';
  
  // Limit description length for SEO
  return description.length > 160 ? description.substring(0, 157) + '...' : description;
}

/**
 * Generate SEO-optimized title for stores
 */
export function generateStoreSeoTitle(storeName: string, location?: string): string {
  let title = `${storeName} - Dyqan Online`;
  
  if (location) {
    title += ` në ${location}`;
  }
  
  title += ' | TreguIm';
  
  return title.length > 60 ? title.substring(0, 57) + '...' : title;
}

/**
 * Generate SEO-optimized description for stores
 */
export function generateStoreSeoDescription(
  storeName: string, 
  storeDescription?: string, 
  location?: string
): string {
  let description = `Shesblej dhe merrjep nga dyqani online ${storeName}`;
  
  if (location) {
    description += ` në ${location}`;
  }
  
  if (storeDescription) {
    // Add truncated store description
    const truncatedDesc = storeDescription.length > 60 ? 
      storeDescription.substring(0, 57) + '...' : storeDescription;
    description += `. ${truncatedDesc}`;
  }
  
  description += ' Celsi produktet më të mira dhe njoftime falas në TreguIm.';
  
  return description.length > 160 ? description.substring(0, 157) + '...' : description;
}

/**
 * Generate keywords for products based on content
 */
export function generateProductKeywords(
  productTitle: string,
  category?: string,
  subcategory?: string,
  location?: string,
  condition?: string,
  make?: string,
  model?: string
): string {
  const keywords: string[] = [];
  
  // Add product title words
  const titleWords = productTitle.toLowerCase().split(' ').filter(word => word.length > 2);
  keywords.push(...titleWords);
  
  // Add new Albanian action keywords
  keywords.push(`shesblej ${productTitle.toLowerCase()}`);
  keywords.push(`merrjep ${productTitle.toLowerCase()}`);
  keywords.push(`${productTitle.toLowerCase()} celsi`);
  keywords.push(`njoftime ${productTitle.toLowerCase()}`);
  
  // Add category keywords
  if (category) {
    keywords.push(category.toLowerCase());
    keywords.push(`${category.toLowerCase()} ne shitje`);
    keywords.push(`shesblej ${category.toLowerCase()}`);
    keywords.push(`merrjep ${category.toLowerCase()}`);
    keywords.push(`celsi ${category.toLowerCase()}`);
  }
  
  if (subcategory) {
    keywords.push(subcategory.toLowerCase());
  }
  
  // Add location-specific keywords
  if (location) {
    keywords.push(location.toLowerCase());
    keywords.push(`${productTitle.toLowerCase()} ${location.toLowerCase()}`);
    keywords.push(`shesblej ${location.toLowerCase()}`);
    keywords.push(`merrjep ${location.toLowerCase()}`);
  }
  
  // Add condition keywords
  if (condition) {
    if (condition === 'new') {
      keywords.push('i ri', 'e re', 'si i ri');
    } else {
      keywords.push('i perdorur', 'e perdorur', 'ne gjendje te mire');
    }
  }
  
  // Add vehicle-specific keywords
  if (make && model) {
    keywords.push(`${make.toLowerCase()} ${model.toLowerCase()}`);
    keywords.push(`${make.toLowerCase()}`);
    keywords.push(`${model.toLowerCase()}`);
  }
  
  // Add enhanced action keywords
  keywords.push(`blej ${productTitle.toLowerCase()}`);
  keywords.push(`${productTitle.toLowerCase()} okazion`);
  keywords.push(`njoftime falas ${productTitle.toLowerCase()}`);
  
  return keywords.join(', ');
}

/**
 * Generate keywords for stores
 */
export function generateStoreKeywords(
  storeName: string,
  storeDescription?: string,
  location?: string
): string {
  const keywords: string[] = [];
  
  // Add store name variations
  const storeWords = storeName.toLowerCase().split(' ').filter(word => word.length > 2);
  keywords.push(...storeWords);
  keywords.push(`dyqan ${storeName.toLowerCase()}`);
  keywords.push(`${storeName.toLowerCase()} online`);
  
  // Add new Albanian keywords for stores
  keywords.push(`shesblej ${storeName.toLowerCase()}`);
  keywords.push(`merrjep ${storeName.toLowerCase()}`);
  keywords.push(`celsi ${storeName.toLowerCase()}`);
  keywords.push(`njoftime ${storeName.toLowerCase()}`);
  
  // Extract keywords from description
  if (storeDescription) {
    const descWords = storeDescription.toLowerCase()
      .split(/\s+/)
      .filter(word => word.length > 3 && !['qene', 'jane', 'kemi', 'para', 'mund'].includes(word))
      .slice(0, 10); // Limit to first 10 relevant words
    keywords.push(...descWords);
  }
  
  if (location) {
    keywords.push(location.toLowerCase());
    keywords.push(`dyqan ${location.toLowerCase()}`);
    keywords.push(`shesblej ${location.toLowerCase()}`);
    keywords.push(`merrjep ${location.toLowerCase()}`);
  }
  
  // Add general store keywords with new terms
  keywords.push('dyqan online', 'shitje online', 'produkte online', 'oferta speciale');
  keywords.push('njoftime falas', 'celsi dyqan', 'shesblej online', 'merrjep produkte');
  
  return keywords.join(', ');
}

/**
 * Create breadcrumb data for SEO
 */
export function createProductBreadcrumbs(
  productTitle: string,
  productId: string | number,
  category?: string,
  subcategory?: string
): Array<{name: string, url: string}> {
  const breadcrumbs = [
    { name: 'TreguIm', url: 'https://treguim.org/' }
  ];
  
  if (category) {
    breadcrumbs.push({
      name: category,
      url: `https://treguim.org/category/${category.toLowerCase().replace(/\s+/g, '-')}`
    });
  }
  
  if (subcategory) {
    breadcrumbs.push({
      name: subcategory,
      url: `https://treguim.org/category/${category?.toLowerCase().replace(/\s+/g, '-')}?subcategory=${subcategory.toLowerCase().replace(/\s+/g, '-')}`
    });
  }
  
  // Add current product (without URL as it's the current page)
  breadcrumbs.push({
    name: productTitle.length > 30 ? productTitle.substring(0, 27) + '...' : productTitle,
    url: `https://treguim.org/product/${productId}`
  });
  
  return breadcrumbs;
}

/**
 * Create breadcrumb data for stores
 */
export function createStoreBreadcrumbs(
  storeName: string,
  storeId: string
): Array<{name: string, url: string}> {
  return [
    { name: 'TreguIm', url: 'https://treguim.org/' },
    { name: 'Dyqanet', url: 'https://treguim.org/stores' },
    { name: storeName, url: `https://treguim.org/store/${storeId}` }
  ];
}
